import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy, Check, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const brl = (n) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(n || 0));
const fmtBR = (iso) => { try { return new Date(iso + "T12:00:00").toLocaleDateString("pt-BR"); } catch { return iso; } };

// ─────────────────────────────────────────────────────────────────────────────
// Página pública de pagamento de uma PARCELA do crediário (Pendente programado)
// Cliente recebe o link via WhatsApp e abre aqui pra copiar PIX/chave.
// ─────────────────────────────────────────────────────────────────────────────
export default function PagamentoParcela() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedBr, setCopiedBr] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/public/installment/${token}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.status === 404 ? "Parcela não encontrada" : "Erro ao carregar");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);
  // Auto-refresh para detectar quando o lojista marcar como paga
  useEffect(() => {
    const i = setInterval(load, 25000);
    return () => clearInterval(i);
    /* eslint-disable-next-line */
  }, [token]);

  const copyVal = async (val, kind) => {
    try {
      await navigator.clipboard.writeText(val);
      kind === "br" ? setCopiedBr(true) : setCopiedKey(true);
      toast.success("Copiado!");
      setTimeout(() => { setCopiedBr(false); setCopiedKey(false); }, 2200);
    } catch { toast.error("Não foi possível copiar"); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Carregando…</div>;
  if (error)   return <div className="min-h-screen flex items-center justify-center text-red-600">{error}</div>;
  if (!data)   return null;

  const { installment, plan, customer_name, store, pix } = data;
  const isPaid = installment.status === "paid";

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#0A0A0A] py-8 px-3">
      <div className="max-w-md mx-auto space-y-4">
        {/* Header com loja */}
        <div className="text-center">
          {store?.logo && <img src={store.logo} alt={store.name} className="h-14 mx-auto mb-2 object-contain"/>}
          <h1 className="font-display text-2xl">{store?.name || "Pagamento"}</h1>
          {store?.phone && <p className="text-xs text-neutral-500">{store.phone}</p>}
        </div>

        <Card className="p-5 space-y-3 border-[#E5E5E5] shadow-none">
          <div className="text-center">
            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs uppercase tracking-wider ${isPaid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
              {isPaid ? <CheckCircle2 size={12}/> : <Clock size={12}/>}
              {isPaid ? "Pagamento confirmado" : "Aguardando pagamento"}
            </div>
            <div className="text-3xl font-display mt-3">{brl(installment.remaining || installment.amount)}</div>
            <div className="text-xs text-neutral-500 mt-1">
              Parcela {installment.index}/{installment.total} · vence {fmtBR(installment.due_date)}
            </div>
            {customer_name && <div className="text-xs text-neutral-500 mt-2">Cliente: <strong>{customer_name}</strong></div>}
            {plan?.order_number && <div className="text-[10px] text-neutral-400">Pedido #{String(plan.order_number).padStart(6,"0")}</div>}
          </div>

          {!isPaid && pix?.brcode && (
            <div className="border-t pt-4 space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Pix copia e cola</div>
                <div className="font-mono text-[10px] break-all bg-neutral-50 dark:bg-white/5 border border-[#E5E5E5] rounded p-2 max-h-24 overflow-y-auto">
                  {pix.brcode}
                </div>
                <Button onClick={()=>copyVal(pix.brcode, "br")} className="w-full mt-2 bg-[#0A0A0A] hover:bg-black text-white" data-testid="copy-brcode">
                  {copiedBr ? <><Check size={14} className="mr-1.5"/> Código copiado</> : <><Copy size={14} className="mr-1.5"/> Copiar Pix copia e cola</>}
                </Button>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Chave PIX</div>
                <div className="flex items-center gap-2">
                  <code className="text-xs font-mono flex-1 truncate">{pix.key}</code>
                  <Button size="sm" variant="outline" onClick={()=>copyVal(pix.key, "key")} data-testid="copy-key">
                    {copiedKey ? <Check size={12}/> : <Copy size={12}/>}
                  </Button>
                </div>
                {pix.merchant_name && <div className="text-[10px] text-neutral-400 mt-1">Beneficiário: {pix.merchant_name}</div>}
              </div>
              <div className="text-[11px] text-neutral-500 text-center pt-2 border-t border-dashed">
                📌 Após pagar, envie o comprovante para a loja pelo WhatsApp.
              </div>
            </div>
          )}

          {isPaid && (
            <div className="text-center text-sm text-emerald-700 py-3">
              ✓ Esta parcela já foi quitada. Obrigado!
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
